'use client';
import React from 'react';
import { BzImage } from '@benzinga/image';
import classNames from 'classnames';
import styled from '@benzinga/themetron';
import { convertToAbsoluteUrl, getHolidayLogo } from '../../utils/holidays';
import { checkDeviceType } from '@benzinga/device-utils';

export type BenzingaLogoVariant = 'light' | 'dark' | 'holiday';

export interface BenzingaLogoProps {
  className?: string;
  variant?: BenzingaLogoVariant;
  deviceType?: 'desktop' | 'mobile' | 'presentation';
  height?: string | number;
  reactiveHolidaysEnabled?: boolean;
}

export const BenzingaLogo: React.FC<BenzingaLogoProps> = ({
  className = '',
  deviceType = 'presentation',
  height = '100%',
  reactiveHolidaysEnabled = false,
  variant = 'light',
}) => {
  const fillColor = variant === 'dark' ? '#093451' : '#FFFFFF';
  const holidayLogoFullUrl = deviceType !== 'presentation' ? getHolidayLogo(deviceType) : null;
  const holidayLogoPath = holidayLogoFullUrl ? convertToAbsoluteUrl(holidayLogoFullUrl) : undefined;
  const [displayDefaultImage, setDisplayDefaultImage] = React.useState(true);

  const combinedClassName = classNames(`benzinga-logo`, {
    [`${className}`]: !!className,
    [`${variant}`]: !!variant,
    [`${deviceType}`]: !!deviceType,
    'reactive-holiday-logo': reactiveHolidaysEnabled && holidayLogoPath,
  });

  React.useEffect(() => {
    if (!reactiveHolidaysEnabled) return;

    const isMobile = checkDeviceType().isMobile();
    if (isMobile && deviceType !== 'mobile') return;

    const testImageUrl = (url?: string) => {
      return new Promise(function (resolve, reject) {
        const image = new Image();
        image.addEventListener('load', resolve);
        image.addEventListener('error', reject);
        image.src = String(url);
      });
    };
    if (holidayLogoPath !== undefined) {
      testImageUrl(holidayLogoPath)
        .then(() => {
          setDisplayDefaultImage(false);
        })
        .catch(() => {
          setDisplayDefaultImage(true);
        });
    } else {
      setDisplayDefaultImage(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const DefaultLogo = () => (
    <IconWrapper className={combinedClassName}>
      <svg height={height} role="img" viewBox="0 0 471.1 63.8" width="100%" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M67.7 63.8h45.2V51.9H81.4V37.3h26.1V25.4H81.4V11.9h31.7V0H67.7zm99.6-23.6L137.7 0h-20.4l6.4 8.1V64h13.6V22.1l30.6 41.7H181V0h-13.7zM47.1 31c6-1.1 11-6.7 11-14.6C58.1 7.9 51.9 0 39.6 0H0l6.2 7.9v56H41c12.3 0 18.7-7.7 18.7-17.3-.3-7.9-5.7-14.7-12.6-15.6zM19.8 12h17.1c4.6 0 7.5 2.7 7.5 6.7s-2.9 6.7-7.5 6.7H19.8V12zm17.7 40H19.8V37.3h17.7c5.2 0 8.1 3.3 8.1 7.3 0 4.6-3.1 7.3-8.1 7.4zm343.3-11.8h7.1v7.5c-3.7 3-8.3 4.7-13.1 4.8-11.4 0-19.4-8.7-19.4-20.4s8.1-20.4 19.4-20.4c5.9 0 11.4 3.1 14.6 8.1l10.8-6C395.6 6.5 387.5 0 374.7 0c-18.1 0-33.1 12.5-33.1 31.9s14.8 31.9 33.1 31.9c10 .2 19.5-4.1 26.1-11.5V28.7h-29.2l9.2 11.5zm-61.5 0L289.6 0h-20.3l6.3 8.1V64h13.7V22.1l30.6 41.7h13V0h-13.6zM446 0h-26.3l6.3 8.1-1.1 3.1L404.5 64h15.4l4-10.8h27.5l4 10.8h15.4L446 0zm-18.6 41.2l10-27.5 10 27.5h-20zM247.7 0h13.7v64.1h-13.7zm-10.9 11V0h-48.1v11.9h30.2l-30.2 41v10.9h48.6V51.9h-30.9z"
          fill={fillColor}
        ></path>
      </svg>
    </IconWrapper>
  );

  if (displayDefaultImage) {
    return <DefaultLogo />;
  }

  if (reactiveHolidaysEnabled && holidayLogoPath) {
    return (
      <BzImage
        alt="Benzinga Holiday Logo"
        className={combinedClassName}
        height={45}
        loader={({ src }) => src}
        src={holidayLogoPath}
        width={192}
      />
    );
  }

  return <DefaultLogo />;
};

const IconWrapper = styled.span`
  &.benzinga-logo {
    display: inline-block;

    &.desktop {
      width: 192px;
      min-width: 192px;
      max-width: 192px;
    }

    img,
    svg {
      width: 100%;
      height: 100%;
    }
  }
`;

export default BenzingaLogo;
